import React from "react";
import {
  ArrowRight,
  MessageCircle,
  Check,
  Sparkles,
} from "lucide-react";
import { BOOK_CALL_URL, WHATSAPP_URL } from "@/lib/constants";

const chips = [
  "Recovers lost leads",
  "Auto follow-ups",
  "Converts conversations",
  "Drives repeat sales",
];

export default function Hero() {
  return (
    <section
      id="top"
      data-testid="hero-section"
      className="relative overflow-hidden"
      style={{ background: "var(--ink)" }}
    >
      <div className="grain absolute inset-0" />
      <div className="hero-glow" />

      {/* Decorative SVG wave lines */}
      <svg
        className="absolute inset-0 w-full h-full opacity-[0.08] pointer-events-none"
        viewBox="0 0 1440 900"
        preserveAspectRatio="none"
        fill="none"
      >
        <path
          d="M-100 700 Q 300 500 720 650 T 1540 520"
          stroke="#e8c279"
          strokeWidth="1"
        />
        <path
          d="M-100 780 Q 400 580 780 720 T 1540 600"
          stroke="#e8c279"
          strokeWidth="1"
        />
        <path
          d="M-100 200 Q 400 380 820 220 T 1540 300"
          stroke="#25d366"
          strokeWidth="1"
        />
      </svg>

      <div className="relative max-w-7xl mx-auto px-6 lg:px-10 pt-36 pb-28 lg:pt-44 lg:pb-36">
        <div
          className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full border border-[var(--gold)]/30 reveal"
          style={{ background: "rgba(212,164,76,0.08)" }}
          data-testid="hero-eyebrow"
        >
          <Sparkles size={14} className="text-[var(--gold-light)]" />
          <span
            className="text-xs font-medium uppercase tracking-[0.18em]"
            style={{ color: "var(--gold-light)" }}
          >
            WhatsApp Revenue Multiplier System
          </span>
        </div>

        <h1
          className="font-display mt-8 text-white text-4xl sm:text-5xl lg:text-[68px] leading-[1.05] max-w-5xl reveal"
          style={{ animationDelay: "80ms" }}
          data-testid="hero-headline"
        >
          Bought WhatsApp API…{" "}
          <span className="font-display-italic">but it isn’t generating revenue?</span>
        </h1>

        <p
          className="mt-7 text-base sm:text-lg lg:text-xl max-w-2xl leading-relaxed reveal"
          style={{ color: "rgba(248,241,231,0.72)", animationDelay: "160ms" }}
          data-testid="hero-subheadline"
        >
          You don’t need another tool. You need to turn WhatsApp into a{" "}
          <span className="font-display-italic text-[var(--gold-light)]">
            Revenue Multiplier™
          </span>{" "}
          — a done-for-you system that recovers lost leads, follows up
          automatically, and converts conversations into clients.
        </p>

        <div
          className="mt-10 flex flex-wrap items-center gap-3 sm:gap-4 reveal"
          style={{ animationDelay: "240ms" }}
          data-testid="hero-cta-row"
        >
          <a
            href={BOOK_CALL_URL}
            target="_blank"
            rel="noreferrer"
            data-testid="hero-book-call-btn"
            className="group inline-flex items-center gap-2 px-6 sm:px-7 py-3.5 rounded-full font-semibold text-sm sm:text-base transition-all hover:scale-[1.03]"
            style={{ background: "var(--gold)", color: "var(--ink)" }}
          >
            Book a Strategy Call
            <ArrowRight
              size={18}
              className="transition-transform group-hover:translate-x-0.5"
            />
          </a>
          <a
            href={WHATSAPP_URL}
            target="_blank"
            rel="noreferrer"
            data-testid="hero-whatsapp-btn"
            className="inline-flex items-center gap-2 px-6 sm:px-7 py-3.5 rounded-full font-semibold text-sm sm:text-base text-white transition-all hover:scale-[1.03]"
            style={{ background: "var(--whatsapp)" }}
          >
            <MessageCircle size={18} strokeWidth={2.5} />
            Chat on WhatsApp
          </a>
          <a
            href="#system"
            data-testid="hero-see-how-btn"
            className="inline-flex items-center gap-2 px-6 py-3.5 rounded-full font-semibold text-sm sm:text-base transition-all hover:bg-white/5"
            style={{
              color: "rgba(248,241,231,0.85)",
              border: "1px solid rgba(248,241,231,0.18)",
            }}
          >
            See How It Works
          </a>
        </div>

        <div
          className="mt-14 grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4 max-w-4xl reveal"
          style={{ animationDelay: "320ms" }}
          data-testid="hero-chips"
        >
          {chips.map((c, i) => (
            <div
              key={c}
              className="flex items-center gap-2.5 rounded-xl px-4 py-3.5 border"
              style={{
                background: "rgba(255,255,255,0.03)",
                borderColor: "rgba(255,255,255,0.08)",
                backdropFilter: "blur(10px)",
              }}
              data-testid={`hero-chip-${i}`}
            >
              <span
                className="w-6 h-6 rounded-full inline-flex items-center justify-center shrink-0"
                style={{ background: "rgba(37,211,102,0.15)" }}
              >
                <Check size={13} className="text-[var(--whatsapp)]" strokeWidth={3} />
              </span>
              <span className="text-xs sm:text-sm text-cream/85" style={{ color: "rgba(248,241,231,0.85)" }}>
                {c}
              </span>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}
